import { computed, Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Option } from '@sapphire/result';

import {
  Qualification,
  RESPONDENT_LIST_ORIGIN,
  SelectableQualificationDatum,
  SurveyLocaleData,
  AdvancedQualification,
} from '@ps/survey-utils';
import { Undefinable } from '@ps/utils';

import {
  selectAdvancedFeasibilityFilters,
  selectAudienceChildren,
  selectHasChildSurveys,
  selectSurveyClickBalancing,
  selectLocationFileUpload,
  selectQuotasForm,
  selectLinkedTagIds,
  selectLoadingLinkedTags,
  selectLinkTagsError,
  selectSuppliersMetaDataBlendId,
  selectSuppliersMetaData,
  selectIsDirty,
  selectCanUpdateTargetMargin,
  selectIsMetadataValid,
  selectMetadataCompletes,
  selectMetadataLoi,
  selectMetadataIncidence,
  selectMetadataFieldTime,
  selectMetadataForm,
  selectIsPreDraft,
  selectIsDrawablePreDraft,
  selectSurveyFormState,
} from './audience/survey-creation-audience.selectors';
import {
  selectSurvey,
  selectSurveyId,
  selectRootSurveyId,
  selectRootSurvey,
  selectError,
  selectCoreChildren,
  selectParticipantLocales,
  selectKind,
  selectActiveChildKey,
  selectTrackerConfig,
} from './core/survey-creation-core.selectors';
import {
  selectScorePercentage,
  selectScoreLoading,
  selectScoreError,
  selectFeasibilityLoading,
  selectFeasibilityError,
  selectSimilarSurveys,
  selectSimilarSurveysError,
  selectSimilarSurveysGlobalSearchUsed,
  selectSimilarSurveysHasSearched,
  selectSimilarSurveysSearchedGlobally,
  selectSimilarSurveysStale,
  selectSimilarSurveysStatus,
  selectFeasibilityCpi,
  selectFeasibilitySuppliers,
  selectFeasibility,
  selectDifficultQuotas,
  selectImprobableQuotas,
  selectAdvancedFiltersLookup,
  selectFlexibilityPrices,
  selectFlexibilityPricesError,
  selectEpcValues,
  selectInsightsChildren,
  selectPricing,
} from './insights/survey-creation-insights.selectors';
import {
  selectQualificationsCatalogRecord,
  selectQualificationsRecord,
  selectQualificationsRecordForLocales,
  selectQuestionCategories,
  selectIsQuestionCategoriesLoading,
  selectQuestionsRecord,
  selectQuestionsLoadingKey,
  selectLocationConditionsRecord,
  selectLocationConditionsLoadingKey,
} from './reference/survey-creation-reference.selectors';
import {
  selectAidaEligibility,
  selectIsLaunching,
  selectIsLaunchPricingLoading,
  selectLaunchPricing,
  selectLaunchForm,
  selectRespondentListUpload,
} from './launch/survey-creation-launch.selectors';
import { selectLaunchPrerequisiteError } from './core/utils/survey-save-pipeline.helper';
import {
  locationConditionsCacheKey,
  resolveQualifications,
  surveyAdvancedQualificationCacheKey,
} from '../utils';

@Injectable({ providedIn: 'root' })
export class SurveyCreationSelectorsFacade {
  private readonly _store = inject(Store);

  public readonly surveyId = this._store.selectSignal(selectSurveyId);
  public readonly rootSurveyId = this._store.selectSignal(selectRootSurveyId);

  // State holds `Survey | null` (Option is not serializable); the facade is the
  // boundary where that becomes an Option for everything above it.
  private readonly _survey = this._store.selectSignal(selectSurvey);
  // Data used to populate the form
  public readonly survey = computed(() => Option.from(this._survey()));

  private readonly _rootSurvey = this._store.selectSignal(selectRootSurvey);
  public readonly rootSurvey = computed(() => Option.from(this._rootSurvey()));

  public readonly error = this._store.selectSignal(selectError);

  public readonly isMetadataValid = this._store.selectSignal(
    selectIsMetadataValid,
  );

  // The live completes (goal) from the audience form — reflects edits before
  // save, unlike `survey.completesRequired`.
  public readonly metadataCompletes = this._store.selectSignal(
    selectMetadataCompletes,
  );

  // Live LOI/IR from the audience form — reflect edits before save, unlike
  // `survey.expectedLoi`/`survey.expectedIr`.
  public readonly metadataLoi = this._store.selectSignal(selectMetadataLoi);
  public readonly metadataIncidence = this._store.selectSignal(
    selectMetadataIncidence,
  );
  public readonly metadataFieldTime = this._store.selectSignal(
    selectMetadataFieldTime,
  );

  public readonly metadataForm = this._store.selectSignal(selectMetadataForm);

  public readonly isPreDraft = this._store.selectSignal(selectIsPreDraft);

  public readonly isDrawablePreDraft = this._store.selectSignal(
    selectIsDrawablePreDraft,
  );

  public readonly audienceChildren = this._store.selectSignal(
    selectAudienceChildren,
  );

  public readonly coreChildren = this._store.selectSignal(selectCoreChildren);

  public readonly surveyFormState = this._store.selectSignal(
    selectSurveyFormState,
  );

  public readonly pricing = this._store.selectSignal(selectPricing);

  public readonly kind = this._store.selectSignal(selectKind);

  public readonly trackerConfig = this._store.selectSignal(selectTrackerConfig);

  public readonly launchForm = this._store.selectSignal(selectLaunchForm);

  private readonly _launchPricing =
    this._store.selectSignal(selectLaunchPricing);
  public readonly launchPricing = computed(() =>
    Option.from(this._launchPricing()),
  );

  public readonly isLaunchPricingLoading = this._store.selectSignal(
    selectIsLaunchPricingLoading,
  );

  public readonly isLaunching = this._store.selectSignal(selectIsLaunching);

  private readonly _launchPrerequisiteError = this._store.selectSignal(
    selectLaunchPrerequisiteError,
  );
  public readonly launchPrerequisiteError = computed(() =>
    Option.from(this._launchPrerequisiteError()),
  );

  private readonly _aidaEligibility = this._store.selectSignal(
    selectAidaEligibility,
  );
  public readonly aidaEligibility = computed(() =>
    Option.from(this._aidaEligibility()),
  );

  public readonly respondentListUpload = this._store.selectSignal(
    selectRespondentListUpload,
  );

  public readonly isRespondentListSurveyGrouped = computed(
    () =>
      this.respondentListUpload().origin ===
      RESPONDENT_LIST_ORIGIN.SURVEY_GROUPING,
  );

  public readonly activeChildKey =
    this._store.selectSignal(selectActiveChildKey);

  public readonly hasChildSurveys = this._store.selectSignal(
    selectHasChildSurveys,
  );

  public readonly insightsChildren = this._store.selectSignal(
    selectInsightsChildren,
  );

  public readonly surveyClickBalancing = this._store.selectSignal(
    selectSurveyClickBalancing,
  );

  private readonly _quotasForm = this._store.selectSignal(selectQuotasForm);

  public readonly quotas = computed(() => this._quotasForm().value);

  public readonly hasQuotas = computed(
    () => this._quotasForm().value.length > 0,
  );

  public readonly locationFileUpload = this._store.selectSignal(
    selectLocationFileUpload,
  );

  public readonly linkedTagIds = this._store.selectSignal(selectLinkedTagIds);

  public readonly loadingLinkedTags = this._store.selectSignal(
    selectLoadingLinkedTags,
  );

  public readonly linkTagsError = this._store.selectSignal(selectLinkTagsError);

  // The survey's persisted supplier-blend id; feeds the blend pickers so a
  // re-opened survey preselects its saved blend. Plain (not Option): both
  // consumers want a raw Nullable<string>.
  public readonly suppliersMetaDataBlendId = this._store.selectSignal(
    selectSuppliersMetaDataBlendId,
  );

  // The survey's persisted supplier pricing facts (global override, pause
  // threshold, pure-price/rate-card flags, manual override) — the panel reads
  // this to pick its initial CPI mode.
  public readonly suppliersMetaData = this._store.selectSignal(
    selectSuppliersMetaData,
  );

  //SECTION Score
  public readonly scorePercentage = this._store.selectSignal(
    selectScorePercentage,
  );

  public readonly scoreLoading = this._store.selectSignal(selectScoreLoading);

  public readonly scoreError = this._store.selectSignal(selectScoreError);
  //!SECTION

  //SECTION Feasibility
  public readonly feasibilityLoading = this._store.selectSignal(
    selectFeasibilityLoading,
  );

  public readonly feasibilityError = this._store.selectSignal(
    selectFeasibilityError,
  );

  public readonly feasibilityCpi =
    this._store.selectSignal(selectFeasibilityCpi);

  public readonly feasibilitySuppliers = this._store.selectSignal(
    selectFeasibilitySuppliers,
  );

  /** The estimate the last recalculation produced. */
  public readonly feasibility = this._store.selectSignal(selectFeasibility);

  /** Quotas the last estimate flagged as difficult to field. */
  public readonly difficultQuotas = this._store.selectSignal(
    selectDifficultQuotas,
  );

  /** Quotas an operator's unicorn record matched — improbable to field. */
  public readonly improbableQuotas = this._store.selectSignal(
    selectImprobableQuotas,
  );

  public readonly improbableQuotaIds = computed<readonly string[]>(() =>
    this.improbableQuotas().map((quota) => quota.buyerQuotaId),
  );

  public readonly improbableQualificationCodes = computed<ReadonlySet<number>>(
    () =>
      new Set(
        this.improbableQuotas().flatMap((quota) => quota.qualificationCodes),
      ),
  );

  public readonly advancedFilters = this._store.selectSignal(
    selectAdvancedFeasibilityFilters,
  );

  public readonly advancedFiltersLookup = this._store.selectSignal(
    selectAdvancedFiltersLookup,
  );
  //!SECTION

  //SECTION Similar Surveys
  public readonly similarSurveys =
    this._store.selectSignal(selectSimilarSurveys);

  private readonly _similarSurveysStatus = this._store.selectSignal(
    selectSimilarSurveysStatus,
  );

  public readonly similarSurveysLoading = computed(
    () => this._similarSurveysStatus() === 'loading',
  );

  public readonly similarSurveysError = this._store.selectSignal(
    selectSimilarSurveysError,
  );

  public readonly similarSurveysStale = this._store.selectSignal(
    selectSimilarSurveysStale,
  );

  public readonly similarSurveysHasSearched = this._store.selectSignal(
    selectSimilarSurveysHasSearched,
  );

  public readonly similarSurveysSearchedGlobally = this._store.selectSignal(
    selectSimilarSurveysSearchedGlobally,
  );

  public readonly similarSurveysGlobalSearchUsed = this._store.selectSignal(
    selectSimilarSurveysGlobalSearchUsed,
  );
  //!SECTION

  //SECTION Flexibility Prices
  private readonly _flexibilityPrices = this._store.selectSignal(
    selectFlexibilityPrices,
  );
  public readonly flexibilityPrices = computed(() =>
    Option.from(this._flexibilityPrices()),
  );

  public readonly flexibilityPricesError = this._store.selectSignal(
    selectFlexibilityPricesError,
  );

  public readonly isDirty = this._store.selectSignal(selectIsDirty);

  public readonly canUpdateTargetMargin = this._store.selectSignal(
    selectCanUpdateTargetMargin,
  );

  public readonly epcValues = this._store.selectSignal(selectEpcValues);

  //!SECTION

  private readonly _qualificationsRecord = this._store.selectSignal(
    selectQualificationsRecord,
  );

  public readonly qualificationsRecord = this._store.selectSignal(
    selectQualificationsCatalogRecord,
  );

  private readonly _participantLocales = this._store.selectSignal(
    selectParticipantLocales,
  );

  public readonly participantQualificationsRecord = computed(() =>
    this._store.selectSignal(
      selectQualificationsRecordForLocales(this._participantLocales()),
    )(),
  );

  // Advanced Targeting (definition builder question search) reference data
  public readonly questionCategories = this._store.selectSignal(
    selectQuestionCategories,
  );

  public readonly isQuestionCategoriesLoading = this._store.selectSignal(
    selectIsQuestionCategoriesLoading,
  );

  private readonly _questionsRecord = this._store.selectSignal(
    selectQuestionsRecord,
  );

  private readonly _questionsLoadingKey = this._store.selectSignal(
    selectQuestionsLoadingKey,
  );

  private readonly _locationConditionsRecord = this._store.selectSignal(
    selectLocationConditionsRecord,
  );

  private readonly _locationConditionsLoadingKey = this._store.selectSignal(
    selectLocationConditionsLoadingKey,
  );

  // Selector methods
  public selectQuestions(
    search: string,
    categoryIds: number[],
    locale: string,
  ): AdvancedQualification[] {
    return (
      this._questionsRecord()[
        surveyAdvancedQualificationCacheKey(search, categoryIds, locale)
      ]?.data ?? []
    );
  }

  public isQuestionsCached(
    search: string,
    categoryIds: number[],
    locale: string,
  ): boolean {
    return !!this._questionsRecord()[
      surveyAdvancedQualificationCacheKey(search, categoryIds, locale)
    ];
  }

  public isQuestionsLoading(
    search: string,
    categoryIds: number[],
    locale: string,
  ): boolean {
    return (
      this._questionsLoadingKey() ===
      surveyAdvancedQualificationCacheKey(search, categoryIds, locale)
    );
  }

  public selectLocationConditions(
    dbQualificationCode: number,
    locale: string,
  ): SelectableQualificationDatum[] {
    return (
      this._locationConditionsRecord()[
        locationConditionsCacheKey(dbQualificationCode, locale)
      ]?.data ?? []
    );
  }

  public isLocationConditionsLoading(
    dbQualificationCode: number,
    locale: string,
  ): boolean {
    return (
      this._locationConditionsLoadingKey() ===
      locationConditionsCacheKey(dbQualificationCode, locale)
    );
  }

  // Presence, not data length: a qualification whose lookup genuinely returns zero rows is still
  // "already tried" and must not keep re-triggering a load (see loadLocationConditions$'s cache
  // gate, which treats a fresh empty result as a valid hit the same way).
  public isLocationConditionsCached(
    dbQualificationCode: number,
    locale: string,
  ): boolean {
    return (
      locationConditionsCacheKey(dbQualificationCode, locale) in
      this._locationConditionsRecord()
    );
  }

  public selectQualifications(
    locale: Undefinable<SurveyLocaleData>,
    recentLocaleWhenNull = false,
  ): Qualification[] {
    return resolveQualifications(
      this._qualificationsRecord(),
      locale,
      recentLocaleWhenNull,
    );
  }
}
